import { isReportCompleted } from "@utils/reportStatus";
import { useNavigateById } from "@utils/utils";
import React from "react";
import { useNavigate } from "react-router-dom";

import { ApiReportSectionsAiGeneratedStatusChoices, ReportPageLocationEnums } from "@api/graphql/types-and-hooks";
import { ReportSection } from "@components/ReportField/ReportSection";
import { AddSection } from "@pages/EvaluationDetails/Components/AddSection";
import {
  FilterSectionSubRoute,
  findErrorSection,
  isGeneratingSection,
  useGetFilteredPageSectionsByLocation,
} from "@pages/EvaluationDetails/Components/helper";
import { getNextRoute } from "@pages/EvaluationDetails/EvaluationDetails";
import { ConfirmGenerationDialog } from "@pages/EvaluationDetails/EvaluationDetailsSubPages/EvaluationUploadFiles/ConfirmGenerationDialog/ConfirmGenerationDialog";
import { FooterButtonRow } from "@pages/EvaluationDetails/EvaluationDetailsSubPages/FooterButtonRow";
import { StickyNav } from "@pages/EvaluationDetails/EvaluationDetailsSubPages/StickyNav/StickyNav";
import { useEvaluationData } from "@providers/EvaluationProvider";
import { useReport } from "@providers/ReportProvider";

const pageLocation: FilterSectionSubRoute = "conclusions";

export const EvaluationConclusions: React.FC = () => {
  const { isBaseTemplate } = useEvaluationData();
  const { report } = useReport();
  const reportIsCompleted = isReportCompleted(report);

  const filteredSections = useGetFilteredPageSectionsByLocation(pageLocation);

  const go = useNavigate();
  const nextPage = getNextRoute(pageLocation) as FilterSectionSubRoute;
  const nextPageFilteredSections = useGetFilteredPageSectionsByLocation(nextPage);
  const [promptDialogOpen, setPromptDialogOpen] = React.useState<boolean>(false);

  const handlePrimaryButtonAction = async () => {
    const nextPageAlreadyGenerated = nextPageFilteredSections.some(
      (section) =>
        section.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPending ||
        section.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPredicted,
    );
    if (!nextPageAlreadyGenerated) setPromptDialogOpen(true);
    else go(`../${nextPage}`);
  };

  const isSectionGenerating = isGeneratingSection(filteredSections);
  const scrollToDivById = useNavigateById();

  return (
    <div className="flex w-full flex-col pb-6">
      <ConfirmGenerationDialog
        type={ReportPageLocationEnums.Recommendations}
        promptDialogOpen={promptDialogOpen}
        setPromptDialogOpen={setPromptDialogOpen}
      />
      <div className="grid grid-cols-4 gap-x-1">
        <div className={`col-span-3 flex w-full flex-col gap-y-4`}>
          {isBaseTemplate && !filteredSections[0] && (
            <AddSection newRankOrder={1} defaultNewSectionTitle="Enter Section Title" pageLocation={pageLocation} />
          )}
          {filteredSections.map((section) => (
            <ReportSection key={section.id} section={section} disabled={reportIsCompleted} />
          ))}
        </div>

        <div className="flex w-full flex-col pl-8">
          <StickyNav state={filteredSections} />
        </div>
      </div>
      <FooterButtonRow
        primaryButtonTitle={"Next"}
        primaryButtonDisabled={isSectionGenerating}
        primaryButtonAction={async () => {
          const errorSection = findErrorSection(filteredSections);
          if (errorSection) {
            scrollToDivById(errorSection.id);
          } else handlePrimaryButtonAction();
        }}
        skipNavigate
      />
    </div>
  );
};
